import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import {
  SpedDocumentInutilizationListRequest,
  SpedDocumentInvoiceListRequest,
  SpedDocumentListRequest,
  SpedDocumentPurchaseListRequest,
} from './sped-documents.types';
import spedDocumentsServices from './sped-documents.services';
import { notification } from 'antd';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import notificationError from '@/utils/functions/notificationError';
import { AxiosError } from 'axios';

export const QUERY_SPED_DOCUMENT_LIST = 'sped-document/list';
export const QUERY_SPED_DOCUMENT_DETAIL = 'sped-document/detail';
export const QUERY_SPED_DOCUMENT_INVOICES = 'sped-document/invoices';
export const QUERY_SPED_DOCUMENT_PURCHASES = 'sped-document/purchases';
export const QUERY_SPED_DOCUMENT_INUTILIZATIONS = 'sped-document/inutilzations';

export function useSpedDocumentList(params?: SpedDocumentListRequest) {
  return useInfiniteQuery({
    queryKey: [QUERY_SPED_DOCUMENT_LIST, params],
    queryFn: ({ pageParam }) =>
      spedDocumentsServices.documentsList({
        size: 10,
        ...params,
        afterCursor: pageParam,
      }),
    enabled: !!params?.companyId,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function useSpedDocumentDetail(id?: number) {
  return useQuery({
    queryKey: [QUERY_SPED_DOCUMENT_DETAIL, id],
    queryFn: () => spedDocumentsServices.documentDetail(id),
    enabled: !!id,
  });
}

export function useSpedDocumentRemove() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: spedDocumentsServices.documentRemove,
    onSuccess() {
      notification.success({ message: 'Documento SPED removido com sucesso!' });
      queryChanel({ invalidateQueries: [[QUERY_SPED_DOCUMENT_LIST]] });
    },
    onError(error: AxiosError) {
      notificationError(error, { description: error?.response?.data?.detail });
    },
  });
}

export function useSpedDocumentCreate() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: spedDocumentsServices.documentCreate,
    onSuccess() {
      notification.success({ message: 'Documento SPED criado com sucesso!' });
      queryChanel({ invalidateQueries: [[QUERY_SPED_DOCUMENT_LIST]] });
    },
    onError(error: AxiosError) {
      notificationError(error, { description: error?.response?.data?.detail });
    },
  });
}

export function useSpedDocumentEdit() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: spedDocumentsServices.documentEdit,
    onSuccess(_, vars) {
      notification.success({ message: 'Documento SPED editado com sucesso!' });
      queryChanel({
        invalidateQueries: [
          [QUERY_SPED_DOCUMENT_LIST],
          [QUERY_SPED_DOCUMENT_DETAIL, vars.spedDocumentId],
        ],
      });
    },
    onError(error: AxiosError) {
      notificationError(error, { description: error?.response?.data?.detail });
    },
  });
}

export function useSpedDocumentInvoices(
  params: SpedDocumentInvoiceListRequest,
) {
  return useQuery({
    queryKey: [QUERY_SPED_DOCUMENT_INVOICES, params],
    queryFn: () => spedDocumentsServices.invoices(params),
    enabled: !!params.spedDocumentId,
  });
}

export function useSpedDocumentManageImports() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: spedDocumentsServices.manageImports,
    onSuccess(_, vars) {
      const messages = {
        PURCHASE: 'Importação de XML de compras no SPED executado com sucesso!',
        INVOICE:
          'Importação de XML de notas fiscais no SPED executado com sucesso!',
        INUTILIZATION:
          'Importação de XML de inutilização de numeração no SPED executado com sucesso!',
        REMOVE: 'Remoção das seleções no SPED executado com sucesso!',
      };

      notification.success({
        message: messages[vars.importType],
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_SPED_DOCUMENT_INVOICES],
          [QUERY_SPED_DOCUMENT_PURCHASES],
          [QUERY_SPED_DOCUMENT_INUTILIZATIONS],
        ],
      });
    },
    onError(error: AxiosError) {
      notificationError(error, { description: error?.response?.data?.detail });
    },
  });
}

export function useSpedDocumentPurchases(
  params: SpedDocumentPurchaseListRequest,
) {
  return useQuery({
    queryKey: [QUERY_SPED_DOCUMENT_PURCHASES, params],
    queryFn: () => spedDocumentsServices.purchases(params),
    enabled: !!params.spedDocumentId,
  });
}

export function useSpedDocumentInutilization(
  params: SpedDocumentInutilizationListRequest,
) {
  return useQuery({
    queryKey: [QUERY_SPED_DOCUMENT_INUTILIZATIONS, params],
    queryFn: () => spedDocumentsServices.inutilizations(params),
    enabled: !!params.spedDocumentId,
  });
}

export function useSpedDocumentSendToPlugStorage() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: spedDocumentsServices.sendToPlugStorage,
    onSuccess() {
      notification.success({
        message: 'Novos documentos foram enviados para apuração!',
      });

      queryChanel({
        invalidateQueries: [
          [QUERY_SPED_DOCUMENT_DETAIL],
          [QUERY_SPED_DOCUMENT_INVOICES],
          [QUERY_SPED_DOCUMENT_PURCHASES],
        ],
      });
    },
    onError(error: AxiosError) {
      notificationError(error, { description: error?.response?.data?.detail });
    },
  });
}
